import React, { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useApp } from '../../context/AppContext';
import KPICard from '../../components/KPICard/KPICard';
import Card from '../../components/Card/Card';
import { StatusBadge } from '../../components/Badge/Badge';
import '../../styles/layout.css';
import '../../styles/tables.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const vBarOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 9.5 }, color: '#8faabb' },
    },
    y: {
      grid: { color: '#f0f3f8' },
      ticks: { font: { size: 10 }, color: '#8faabb', stepSize: 3 },
    },
  },
};

const hBarOpts = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: { label: (ctx) => ` ${ctx.parsed.x.toFixed(1)} h/day` },
    },
  },
  scales: {
    x: {
      grid: { color: '#f0f3f8' },
      min: 0,
      max: 12,
      ticks: { font: { size: 9.5 }, color: '#8faabb' },
    },
    y: {
      grid: { display: false },
      ticks: { font: { size: 9.5 }, color: '#3d5166' },
    },
  },
};

export default function FleetRegistry() {
  const { operator, status, D } = useApp();
  const [search, setSearch] = useState('');

  const fleet = useMemo(() => {
    let f = D.fleet || [];
    if (operator && operator !== 'All') f = f.filter((a) => a.O === operator);
    if (status && status !== 'All') f = f.filter((a) => a.S === status);
    return f;
  }, [D, operator, status]);

  const avgAge = useMemo(() => {
    const withAge = fleet.filter((a) => a.Age);
    if (!withAge.length) return '—';
    return (withAge.reduce((s, a) => s + a.Age, 0) / withAge.length).toFixed(1) + ' yrs';
  }, [fleet]);

  const uniqueOperators = useMemo(() => new Set(fleet.map((a) => a.O)).size, [fleet]);

  const leasedOut = useMemo(() => fleet.filter((a) => a.S === 'On Lease').length, [fleet]);

  // Fleet Age Distribution — 5-year buckets matching the HTML
  const ageDistChartData = useMemo(() => {
    const bucketDefs = [
      { label: '0–5y',   min: 0,  max: 5  },
      { label: '5–10y',  min: 5,  max: 10 },
      { label: '10–15y', min: 10, max: 15 },
      { label: '15–20y', min: 15, max: 20 },
      { label: '20–25y', min: 20, max: 25 },
      { label: '25–30y', min: 25, max: 30 },
      { label: '30–40y', min: 30, max: 40 },
    ];
    const counts = bucketDefs.map(({ min, max }) =>
      fleet.filter((a) => a.Age != null && a.Age >= min && a.Age < max).length
    );
    return {
      labels: bucketDefs.map((b) => b.label),
      datasets: [{
        label: 'Aircraft',
        data: counts,
        backgroundColor: [
          '#1246d6', '#16a34a', '#0698a0', '#d97706',
          '#7c3aed', '#dc2626', '#8faabb',
        ],
        borderRadius: 3,
        borderSkipped: false,
      }],
    };
  }, [fleet]);

  // Avg Daily Hours / Operator — from util data
  const avgHrsPerOpData = useMemo(() => {
    const byOp = {};
    (D.util || []).forEach((r) => {
      if (!r.op) return;
      if (!byOp[r.op]) byOp[r.op] = { sum: 0, n: 0 };
      byOp[r.op].sum += r.hrs || 0;
      byOp[r.op].n += 1;
    });

    // Only include operators present in the filtered fleet
    const fleetOps = new Set(fleet.map((a) => a.O));
    const entries = Object.entries(byOp)
      .filter(([op]) => !operator || operator === 'All' || fleetOps.has(op))
      .map(([op, { sum, n }]) => ({ op, avg: n ? sum / n : 0 }))
      .sort((a, b) => b.avg - a.avg);

    const BAR_COLORS = ['#1246d6','#16a34a','#0698a0','#d97706','#7c3aed','#dc2626','#ea580c','#8faabb'];
    return {
      labels: entries.map((e) => e.op),
      datasets: [{
        label: 'Avg h/day',
        data: entries.map((e) => parseFloat(e.avg.toFixed(2))),
        backgroundColor: entries.map((_, i) => BAR_COLORS[i % BAR_COLORS.length]),
        borderRadius: 3,
        borderSkipped: false,
      }],
    };
  }, [D, fleet, operator]);

  const filtered = useMemo(() => {
    if (!search.trim()) return fleet;
    const q = search.toLowerCase();
    return fleet.filter((a) =>
      [a.R, a.V, a.O, a.S, a.Eng, a.Lessor].some((v) => v && v.toLowerCase().includes(q))
    );
  }, [fleet, search]);

  return (
    <div className="page-content">

      <div className="krow krow-4">
        <KPICard label="Fleet Size"     value={fleet.length}      sub="Total aircraft"       accent="kb" />
        <KPICard label="Avg Fleet Age"  value={avgAge}            sub="Across filtered fleet" accent="kt" />
        <KPICard label="Operators"      value={uniqueOperators}   sub="Active operators"     accent="kp" />
        <KPICard label="Leased Out"     value={leasedOut}         sub="Currently on lease"   accent="kg" />
      </div>

      {/* Fleet Registry table */}
      <Card
        title="Fleet Registry"
        subtitle="Complete aircraft inventory and specifications"
        icon="📋"
        iconClass="ib"
        noPad
        headerRight={
          <div className="sw">
            <span className="si">🔍</span>
            <input
              className="sinp"
              placeholder="Search registry…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      >
        <div className="ts full">
          <table>
            <thead>
              <tr>
                <th>Reg</th>
                <th>Asset Type</th>
                <th>Variant</th>
                <th>MSN</th>
                <th>Status</th>
                <th>Age</th>
                <th>TH</th>
                <th>TC</th>
                <th>Seats</th>
                <th>Util</th>
                <th>Engine</th>
                <th>Lease Status</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', color: 'var(--tx3)', padding: '24px 14px' }}>
                    No aircraft match the current filters.
                  </td>
                </tr>
              ) : (
                filtered.map((a, i) => {
                  const utilRecs = D.util.filter((u) => u.reg === a.R);
                  const totalHrs = Math.round(utilRecs.reduce((s, u) => s + (u.hrs || 0), 0));
                  const totalCyc = Math.round(utilRecs.reduce((s, u) => s + (u.cyc || 0), 0));
                  const avgHrs = utilRecs.length
                    ? (utilRecs.reduce((s, u) => s + (u.hrs || 0), 0) / utilRecs.length).toFixed(1)
                    : '—';
                  return (
                    <tr key={`${a.M || a.R}-${i}`}>
                      <td><strong>{a.R || '—'}</strong></td>
                      <td style={{ color: 'var(--tx2)' }}>{a.AssetType || a.V || '—'}</td>
                      <td>{a.V || '—'}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{a.M || '—'}</td>
                      <td><StatusBadge status={a.S} /></td>
                      <td>{a.Age != null ? a.Age.toFixed(1) : '—'}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{totalHrs > 0 ? totalHrs.toLocaleString() : '—'}</td>
                      <td style={{ fontFamily: 'Space Mono, monospace', fontSize: 11 }}>{totalCyc > 0 ? totalCyc.toLocaleString() : '—'}</td>
                      <td>{a.Seats != null ? a.Seats : '—'}</td>
                      <td style={{ color: avgHrs !== '—' && parseFloat(avgHrs) >= 8 ? '#16a34a' : avgHrs !== '—' && parseFloat(avgHrs) >= 5 ? '#d97706' : '#dc2626', fontWeight: 600, fontSize: 11.5 }}>
                        {avgHrs !== '—' ? `${avgHrs}h/day` : '—'}
                      </td>
                      <td>{a.Eng || '—'}</td>
                      <td style={{ color: 'var(--gn)', fontSize: 11 }}>{a.LeaseStatus || '—'}</td>
                      <td style={{ color: 'var(--tx3)', fontSize: 11 }}>{a.Loc || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Charts below table */}
      <div className="g2">
        <Card title="Fleet Age Distribution" icon="📅" iconClass="ib">
          <div className="cwsm">
            <Bar data={ageDistChartData} options={vBarOpts} />
          </div>
        </Card>

        <Card title="Avg Daily Hours / Operator" icon="⚡" iconClass="it">
          <div className="cwsm">
            <Bar data={avgHrsPerOpData} options={hBarOpts} />
          </div>
        </Card>
      </div>

    </div>
  );
}
